// Phase 6.1.B — Lightweight Nepali (Bikram Sambat) calendar helpers for the
// frontend. Uses `nepali-date-converter` and mirrors the backend FY logic
// (FY runs Shrawan → Asad; quarters Q1=Shrawan,Bhadra,Ashwin etc.)
import NepaliDate from "nepali-date-converter";

export const BS_MONTH_NAMES = [
  "Baisakh", "Jestha", "Asad",
  "Shrawan", "Bhadra", "Ashwin",
  "Kartik", "Mangsir", "Poush",
  "Magh", "Falgun", "Chaitra",
];

export const QUARTERS = {
  1: [4, 5, 6],    // Shrawan, Bhadra, Ashwin
  2: [7, 8, 9],    // Kartik, Mangsir, Poush
  3: [10, 11, 12], // Magh, Falgun, Chaitra
  4: [1, 2, 3],    // Baisakh, Jestha, Asad — FY closing (Asad-end)
};

export function bsMonthName(m) {
  return BS_MONTH_NAMES[(m - 1) % 12] || "";
}

export function quarterOf(m) {
  for (const [q, months] of Object.entries(QUARTERS)) {
    if (months.includes(m)) return Number(q);
  }
  return 0;
}

export function fiscalYearLabel(y, m) {
  const start = m >= 4 ? y : y - 1;
  const end = (start + 1) % 100;
  return `${start}/${String(end).padStart(2, "0")}`;
}

export function adToBs(adDate) {
  // accepts Date, ISO string, or YYYY-MM-DD
  let d;
  if (!adDate) return null;
  if (adDate instanceof Date) d = adDate;
  else d = new Date(adDate.length === 10 ? adDate + "T00:00:00" : adDate);
  if (Number.isNaN(d.getTime())) return null;
  try {
    const bs = new NepaliDate(d);
    const y = bs.getYear();
    const m = bs.getMonth() + 1;
    const day = bs.getDate();
    return {
      bs_year: y,
      bs_month: m,
      bs_month_name: bsMonthName(m),
      bs_day: day,
      bs_quarter: quarterOf(m),
      bs_fiscal_year: fiscalYearLabel(y, m),
      bs_date: `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`,
    };
  } catch (e) {
    return null;
  }
}

export function bsPeriodKey(y, m) {
  return `${y}-${String(m).padStart(2, "0")}`;
}

export function quarterPeriodKey(y, m) {
  const q = quarterOf(m);
  const anchor = m >= 4 ? y : y - 1;
  return `${anchor}-Q${q}`;
}

// Build a list of {value, label} of BS months covering [startKey..endKey] inclusive.
// startKey / endKey are bsPeriodKey strings ('YYYY-MM' BS).
export function bsMonthRange(startY, startM, endY, endM) {
  const out = [];
  let y = startY, m = startM;
  while (y < endY || (y === endY && m <= endM)) {
    out.push({
      value: bsPeriodKey(y, m),
      label: `${bsMonthName(m)} ${y}`,
      bs_year: y,
      bs_month: m,
    });
    m += 1;
    if (m > 12) { m = 1; y += 1; }
  }
  return out;
}

// Last N BS months ending at (refY, refM) — most recent last
export function lastNBsMonths(n, refY, refM) {
  const out = [];
  let y = refY, m = refM;
  for (let i = 0; i < n; i += 1) {
    out.unshift({ value: bsPeriodKey(y, m), label: `${bsMonthName(m)} ${y}`, bs_year: y, bs_month: m });
    m -= 1;
    if (m < 1) { m = 12; y -= 1; }
  }
  return out;
}

export function currentBs() {
  return adToBs(new Date());
}

// Build FY quarter dropdown options for a list of fiscal years
export function bsQuartersForFy(fyLabel) {
  const start = Number(String(fyLabel).split("/")[0]);
  const out = [];
  Object.entries(QUARTERS).forEach(([q, months]) => {
    const anchor = months[0] >= 4 ? start : start + 1;
    out.push({
      value: `${anchor >= start ? start : start + 1}-Q${q}`,
      // Format: FY 2082/83 Q1 (Shrawan-Bhadra-Ashwin)
      label: `FY ${fyLabel} Q${q} (${months.map(bsMonthName).join("-")})`,
      quarter: Number(q),
    });
  });
  // Anchor year handling: months 1..3 (Q4) are anchored at start year because FY runs Shrawan(start)→Asad(start+1);
  // backend's quarter_period_key uses FY-start as anchor. Recompute correctly:
  return Object.entries(QUARTERS).map(([q, months]) => ({
    value: `${start}-Q${q}`,
    label: `FY ${fyLabel} Q${q} (${months.map(bsMonthName).join("-")})`,
    quarter: Number(q),
  }));
}
